import { useEffect, useRef, useState } from 'react';
import {
  Card, CardContent, Stepper, Step, StepLabel, Button, Stack, TextField, MenuItem, Chip, Alert, Box, Typography,
  IconButton, FormControlLabel, Checkbox, Snackbar, CircularProgress, Autocomplete,
} from '@mui/material';
import { Add, Delete, Save, Send, Close } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/PageHeader';
import { FileUploadField } from '../../../components/FileUploadField';
import {
  projectOnboardingApi, PROJECT_TYPES, PROJECT_CATEGORIES, BUSINESS_UNITS, BILLING_TYPES, PROJECT_STATUSES,
  ZONES, CHARGER_TYPES, SCOPE_OPTIONS, PROJECT_DOC_TYPES, type ProjectApplication,
} from '../../../api/projectOnboarding.api';
import { clientsApi } from '../../../api/clients.api';
import { employeesApi } from '../../../api/employees.api';
import { lookupsApi } from '../../../api/lookups.api';
import { extractApiError } from '../../../api/error';

const STEPS = ['Basic', 'Commercial', 'Timeline', 'Stakeholders', 'Geographic', 'OEM/CPO', 'Scope', 'Documents', 'Review'];
const cap = (s?: string | null) => (s ? s.replace(/_/g, ' ') : '');
const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', approved: 'Approved', active: 'Active',
  closed: 'Closed', rejected: 'Rejected', sent_back: 'Sent Back', clarification_required: 'Clarification Required',
};

type Row = Record<string, unknown>;

export function ProjectOnboardingForm() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const id = Number(useParams<{ id: string }>().id);
  const queryKey = ['project-application-edit', id];
  const { data, isLoading } = useQuery({ queryKey, queryFn: () => projectOnboardingApi.get(id) });
  const { data: clients } = useQuery({ queryKey: ['clients-lookup'], queryFn: () => clientsApi.list({ limit: 200 }) });
  const { data: employees } = useQuery({ queryKey: ['employees-lookup'], queryFn: () => employeesApi.list({ limit: 200 }) });
  const { data: geo } = useQuery({ queryKey: ['geo-lookups'], queryFn: () => lookupsApi.all() });

  const [form, setForm] = useState<Partial<ProjectApplication>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef(form);
  formRef.current = form;
  useEffect(() => { if (data) setForm(data); }, [data]);

  const editable = form.status === 'draft' || form.status === 'sent_back' || form.status === 'clarification_required';
  const set = (k: keyof ProjectApplication, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const clientCode = clients?.data.find((c) => c.id === form.client_id)?.clientCode ?? '';

  function payload(autosave: boolean): Record<string, unknown> {
    const f = formRef.current;
    const { id: _id, application_code, status, current_stage, project_id, client_name, client_code, workflow, created_at, ...rest } = f as Record<string, unknown>;
    void _id; void application_code; void status; void current_stage; void project_id; void client_name; void client_code; void workflow; void created_at;
    return { ...rest, __autosave: autosave };
  }

  const saveMut = useMutation({ mutationFn: (a: boolean) => projectOnboardingApi.saveDraft(id, payload(a)), onSuccess: (res, a) => { setForm(res); if (!a) setToast('Draft saved'); }, onError: (e) => setToast(extractApiError(e)) });
  const submitMut = useMutation({ mutationFn: () => projectOnboardingApi.submit(id), onSuccess: (res) => { setForm(res); qc.invalidateQueries({ queryKey }); setSubmitted(true); }, onError: (e) => setToast(extractApiError(e)) });
  const respondMut = useMutation({ mutationFn: () => projectOnboardingApi.respondClarification(id), onSuccess: (res) => { setForm(res); setToast('Re-submitted'); }, onError: (e) => setToast(extractApiError(e)) });

  useEffect(() => {
    if (!editable) return undefined;
    const t = setInterval(() => saveMut.mutate(true), 120_000);
    return () => clearInterval(t);
  }, [editable]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading || !form.id) return <Box p={4} textAlign="center"><CircularProgress /></Box>;

  if (submitted) {
    return (
      <>
        <PageHeader title="Project Onboarding" subtitle={`Application ${form.application_code}`} />
        <Card><CardContent>
          <Alert severity="success" sx={{ mb: 2 }}>Project created successfully and submitted for approval.</Alert>
          <Typography variant="subtitle2" gutterBottom>Recommended next actions</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={() => navigate('/sites')}>Create Site</Button>
            <Button variant="outlined" onClick={() => navigate('/boqs')}>Upload BOQ</Button>
            <Button variant="outlined" onClick={() => navigate('/employees')}>Assign Project Team</Button>
            <Button variant="outlined" onClick={() => navigate('/work-orders')}>Create Work Order</Button>
            <Button variant="outlined" onClick={() => navigate('/materials')}>Create Material Plan</Button>
            <Button variant="contained" onClick={() => navigate('/project-applications')}>Go to Applications</Button>
          </Stack>
        </CardContent></Card>
      </>
    );
  }

  const T = (k: keyof ProjectApplication, label: string, opts: { type?: string } = {}) => (
    <TextField label={label} type={opts.type ?? 'text'} value={(form[k] as string | number) ?? ''} disabled={!editable}
      onChange={(e) => set(k, opts.type === 'number' ? Number(e.target.value) : e.target.value)} sx={{ minWidth: 220 }} InputLabelProps={opts.type === 'date' ? { shrink: true } : undefined} />
  );
  const Sel = (k: keyof ProjectApplication, label: string, options: readonly string[]) => (
    <TextField select label={label} value={(form[k] as string) ?? ''} disabled={!editable} onChange={(e) => set(k, e.target.value)} sx={{ minWidth: 220 }}>
      {options.map((o) => <MenuItem key={o} value={o}>{cap(o)}</MenuItem>)}
    </TextField>
  );
  // Multi-member stakeholder team (e.g. ROMs for North/East/West/South).
  const EmpMultiSel = (k: keyof ProjectApplication, label: string) => (
    <Autocomplete
      multiple size="small" sx={{ minWidth: 280 }} disabled={!editable}
      options={employees?.data ?? []} getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      value={(employees?.data ?? []).filter((emp) => ((form[k] as number[]) ?? []).includes(emp.id))}
      onChange={(_e, v) => set(k, v.map((emp) => emp.id))}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
  const Bool = (k: keyof ProjectApplication, label: string) => (
    <FormControlLabel control={<Checkbox checked={Boolean(form[k])} disabled={!editable} onChange={(e) => set(k, e.target.checked)} />} label={label} />
  );

  const scope = (form.scope_items ?? []) as string[];
  const docs = (form.documents ?? []) as unknown as Row[];
  const setDocs = (rows: Row[]) => set('documents', rows);

  return (
    <>
      <PageHeader title="Project Onboarding" subtitle={`Application ${form.application_code}`}
        action={<Chip color={form.status === 'active' || form.status === 'approved' ? 'success' : form.status === 'rejected' ? 'error' : 'info'} label={STATUS_LABEL[form.status ?? 'draft'] ?? form.status} />} />

      {!editable && form.status !== 'active' && <Alert severity="info" sx={{ mb: 2 }}>This application is <b>{STATUS_LABEL[form.status ?? ''] ?? form.status}</b>. Editing is locked while under review.</Alert>}
      {form.status === 'clarification_required' && <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => respondMut.mutate()}>Re-submit</Button>}>Clarification requested — update and re-submit.</Alert>}

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, flexWrap: 'wrap' }}>{STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}</Stepper>

      <Card><CardContent>
        <Stack spacing={2}>
          {activeStep === 0 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              <TextField label="Project ID" value={form.application_code ?? ''} disabled sx={{ minWidth: 220 }} helperText="Auto-generated on activation: GF-(Client)-PRJ-#####" />
              {T('project_name', 'Project Name *')}
              <TextField select label="Client Name *" value={form.client_id ?? ''} disabled={!editable} onChange={(e) => set('client_id', Number(e.target.value))} sx={{ minWidth: 220 }}>
                {(clients?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
              </TextField>
              <TextField label="Client Code" value={clientCode} disabled sx={{ minWidth: 160 }} />
              {Sel('project_type', 'Project Type *', PROJECT_TYPES)}
              {Sel('project_category', 'Project Category *', PROJECT_CATEGORIES)}
              {Sel('business_unit', 'Business Unit *', BUSINESS_UNITS)}
            </Stack>
          )}
          {activeStep === 1 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('project_value', 'Project Value *', { type: 'number' })}
              {T('currency', 'Currency')}
              {T('margin_pct', 'Margin %', { type: 'number' })}
              {T('expected_profit_pct', 'Expected Profit %', { type: 'number' })}
              {Sel('billing_type', 'Billing Type *', BILLING_TYPES)}
              {T('payment_terms', 'Payment Terms')}
              {Bool('retention_applicable', 'Retention Applicable?')}
              {T('retention_pct', 'Retention %', { type: 'number' })}
            </Stack>
          )}
          {activeStep === 2 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('start_date', 'Project Start Date *', { type: 'date' })}
              {T('planned_end_date', 'Planned End Date *', { type: 'date' })}
              {T('contract_duration_days', 'Contract Duration (Days)', { type: 'number' })}
              {T('client_committed_date', 'Client Committed Date', { type: 'date' })}
              {T('gigforce_committed_date', 'Gigforce Committed Date', { type: 'date' })}
              {Sel('project_status', 'Project Status *', PROJECT_STATUSES)}
            </Stack>
          )}
          {activeStep === 3 && (
            <Stack spacing={2}>
              <Typography variant="subtitle2">Client Stakeholder</Typography>
              <Stack direction="row" flexWrap="wrap" gap={2}>{T('client_pm_name', 'Client PM Name')}{T('client_pm_mobile', 'Client PM Mobile')}{T('client_pm_email', 'Client PM Email')}</Stack>
              <Typography variant="subtitle2">Internal Stakeholder</Typography>
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {EmpMultiSel('project_manager_ids', 'Project Manager(s) *')}
                {EmpMultiSel('regional_ops_manager_ids', 'Regional Operations Manager(s) *')}
                {EmpMultiSel('procurement_spoc_ids', 'Procurement SPOC(s)')}
                {EmpMultiSel('finance_spoc_ids', 'Finance SPOC(s)')}
                {EmpMultiSel('installation_manager_ids', 'Installation Manager(s)')}
              </Stack>
            </Stack>
          )}
          {activeStep === 4 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              <Autocomplete
                multiple size="small" sx={{ minWidth: 320 }} disabled={!editable}
                options={geo?.regions ?? []} getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={(geo?.regions ?? []).filter((r) => ((form.region_ids as number[]) ?? []).includes(r.id))}
                onChange={(_e, v) => set('region_ids', v.map((r) => r.id))}
                renderInput={(p) => <TextField {...p} label="Regions *" placeholder="Select regions" />}
              />
              <Autocomplete
                multiple size="small" sx={{ minWidth: 320 }} disabled={!editable}
                // States filtered to the selected regions (or all when none selected).
                options={(geo?.states ?? []).filter((s) => { const rids = (form.region_ids as number[]) ?? []; return rids.length === 0 || (s.regionId != null && rids.includes(s.regionId)); })}
                getOptionLabel={(o) => o.name} isOptionEqualToValue={(a, b) => a.id === b.id}
                value={(geo?.states ?? []).filter((s) => ((form.state_ids as number[]) ?? []).includes(s.id))}
                onChange={(_e, v) => set('state_ids', v.map((s) => s.id))}
                renderInput={(p) => <TextField {...p} label="States *" placeholder="Select states" />}
              />
              <Autocomplete
                multiple freeSolo size="small" sx={{ minWidth: 320 }} disabled={!editable}
                options={[]} value={(form.cities as string[]) ?? []}
                onChange={(_e, v) => set('cities', (v as string[]).map((c) => c.trim()).filter(Boolean))}
                renderInput={(p) => <TextField {...p} label="Cities" placeholder="Type a city and press Enter" />}
              />
              <Autocomplete
                multiple size="small" sx={{ minWidth: 280 }} disabled={!editable}
                options={[...ZONES]}
                value={(form.zones as string[]) ?? []}
                onChange={(_e, v) => set('zones', v as string[])}
                renderInput={(p) => <TextField {...p} label="Zone(s)" />}
              />
            </Stack>
          )}
          {activeStep === 5 && (
            <Stack direction="row" flexWrap="wrap" gap={2}>
              {T('oem', 'OEM *')}
              {T('cpo', 'CPO *')}
              {T('charger_brand', 'Charger Brand')}
              {Sel('charger_type', 'Charger Type', CHARGER_TYPES)}
            </Stack>
          )}
          {activeStep === 6 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Project Scope (multiple)</Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {SCOPE_OPTIONS.map((s) => (
                  <FormControlLabel key={s} control={<Checkbox size="small" checked={scope.includes(s)} disabled={!editable}
                    onChange={(e) => set('scope_items', e.target.checked ? [...scope, s] : scope.filter((x) => x !== s))} />} label={cap(s)} />
                ))}
              </Stack>
            </Box>
          )}
          {activeStep === 7 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>Record project documents (PO, Work Order, Agreement, BOQ, SOW, Drawings, Project Plan). Files are tracked by URL in the repository.</Alert>
              <Stack spacing={1.5}>
                {docs.map((row, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField select size="small" label="Type" value={(row.document_type as string) ?? ''} disabled={!editable} sx={{ minWidth: 180 }}
                      onChange={(e) => setDocs(docs.map((r, idx) => (idx === i ? { ...r, document_type: e.target.value } : r)))}>
                      {PROJECT_DOC_TYPES.map((d) => <MenuItem key={d} value={d}>{cap(d)}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="File Name" value={(row.file_name as string) ?? ''} disabled={!editable} onChange={(e) => setDocs(docs.map((r, idx) => (idx === i ? { ...r, file_name: e.target.value } : r)))} sx={{ minWidth: 180 }} />
                    <FileUploadField label="File" disabled={!editable} value={(row.file_url as string) ?? null} onChange={(url) => setDocs(docs.map((r, idx) => (idx === i ? { ...r, file_url: url ?? '' } : r)))} />
                    {editable && <IconButton size="small" color="error" onClick={() => setDocs(docs.filter((_, idx) => idx !== i))}><Delete fontSize="small" /></IconButton>}
                  </Stack>
                ))}
              </Stack>
              {editable && <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => setDocs([...docs, { document_type: 'purchase_order' }])}>Add document</Button>}
            </Box>
          )}
          {activeStep === 8 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Review &amp; Submit</Typography>
              <Typography variant="body2" color="text.secondary">Project: <b>{form.project_name}</b> · Client: {clients?.data.find((c) => c.id === form.client_id)?.clientName ?? '—'} · Value: {form.project_value ?? '—'}</Typography>
              <Typography variant="body2" color="text.secondary">Type: {cap(form.project_type)} · Billing: {cap(form.billing_type)} · Scope: {scope.length} items · Documents: {docs.length}</Typography>
              {!form.client_id && <Alert severity="warning">A client must be selected before submission.</Alert>}
              {(!(form.project_manager_ids?.length) || !(form.regional_ops_manager_ids?.length)) && <Alert severity="warning">At least one Project Manager and one Regional Operations Manager are required.</Alert>}
            </Stack>
          )}
        </Stack>

        <Stack direction="row" justifyContent="space-between" mt={3}>
          <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>Previous</Button>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<Close />} color="inherit" onClick={() => navigate('/project-applications')}>Cancel</Button>
            {editable && <Button startIcon={<Save />} variant="outlined" disabled={saveMut.isPending} onClick={() => saveMut.mutate(false)}>Save Draft</Button>}
            {activeStep < STEPS.length - 1 && <Button variant="contained" onClick={() => { if (editable) saveMut.mutate(true); setActiveStep((s) => s + 1); }}>Next</Button>}
            {activeStep === STEPS.length - 1 && editable && <Button startIcon={<Send />} variant="contained" color="success" disabled={submitMut.isPending} onClick={() => submitMut.mutate()}>Submit for Approval</Button>}
          </Stack>
        </Stack>
      </CardContent></Card>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
